import React, { ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button, keyframes, Skeleton, Stack, styled, Tooltip, Typography } from '@mui/material'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { Target } from 'lucide-react'

import { flagPath } from '../../components/entities/CompaniesGrid'
import InfoBlock from '../../pages/components/InfoBlock'
import { FactsTable, FactsTableRow } from '../../pages/components/FactsTable'

type IssueWarrantSummaryComponent = React.FC<{
    additionalRows?: Record<string, ReactNode>
    hasCompanyLink?: boolean
    hasLink?: boolean
    isLoading: boolean
    isSlim?: boolean
    label: ReactNode
    status: ReactNode
    name: string | null
    companyCountryCode: string | null
    typeLabel: 'Emission' | 'Teckningsoption'
    isDetailsPage: boolean
    companyId: string | null
    sizeLabel: ReactNode
    sizeValue: ReactNode
    subPriceValue: ReactNode
    relevantDateLabel: ReactNode
    relevantDateValue: ReactNode
    commitValue: ReactNode
    outcomeValue: ReactNode
    detailLink: string
    noncashConsiderationValue: ReactNode
}>

const fade = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`
// Fade the Skeleton to avoid flickering upon fast load times
const FadedSkeleton = styled(Skeleton)({
    animation: `${fade} 1s ease`,
})

const ValueContainer = ({ children }: { children: ReactNode }) => (
    <div style={{ paddingLeft: '16px', paddingTop: '1px', paddingBottom: '1px' }}>{children}</div>
)

const TooltipValue = ({ children }: { children: ReactNode }) => <span>{children}</span>

export const IssueWarrantSummary: IssueWarrantSummaryComponent = ({
    hasCompanyLink = true,
    hasLink,
    isLoading,
    isSlim,
    label,
    status,
    name,
    companyCountryCode,
    isDetailsPage,
    companyId,
    typeLabel,
    sizeLabel,
    sizeValue,
    subPriceValue,
    relevantDateLabel,
    relevantDateValue,
    commitValue,
    outcomeValue,
    detailLink,
    noncashConsiderationValue,
    additionalRows = {},
}) => {
    const { t } = useTranslation()
    const summaryTitle = t('components.issueWarrantSummary.summary')
    const companyLabel = (
        <Stack
            direction='row'
            spacing={2}
            sx={{
                alignItems: 'center',
            }}
        >
            {companyCountryCode && (
                <Tooltip title={companyCountryCode}>
                    <div style={{ display: 'inline-block' }}>
                        <img src={flagPath(companyCountryCode)} width={25} style={{ display: 'block' }} />
                    </div>
                </Tooltip>
            )}
            {name && <Typography>{name}</Typography>}
        </Stack>
    )

    return (
        <InfoBlock title={isDetailsPage ? summaryTitle : null}>
            <FactsTable isSlim={isSlim}>
                <FactsTableRow label={isSlim ? null : typeLabel} rightColumnWidth={isSlim ? null : undefined}>
                    <Stack spacing={3}>
                        {!isLoading && (
                            <>
                                {hasCompanyLink && companyId && (
                                    <Link to={`/companies/${companyId}`}>{companyLabel}</Link>
                                )}
                                {(!hasCompanyLink || !companyId) && companyLabel}
                                <Stack
                                    direction='row'
                                    spacing={3}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    {label}
                                    {status}
                                </Stack>
                            </>
                        )}
                        {isLoading && (
                            <>
                                <FadedSkeleton variant='rectangular' height={20} width={100} />
                                <FadedSkeleton variant='rectangular' height={20} width={200} />
                            </>
                        )}
                    </Stack>
                </FactsTableRow>
                <FactsTableRow label={isSlim ? null : summaryTitle} rightColumnWidth={isSlim ? null : undefined}>
                    <FactsTable noBorder>
                        <FactsTableRow
                            label={
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                    }}
                                >
                                    <Tooltip title={sizeLabel}>
                                        <AttachMoneyIcon fontSize='small' />
                                    </Tooltip>
                                </Box>
                            }
                            padding='2px 0px'
                            leftColumnWidth={null}
                            rightColumnWidth={null}
                        >
                            <ValueContainer>
                                {isLoading && <FadedSkeleton variant='rectangular' width={100} />}
                                {!isLoading && (
                                    <Tooltip title={sizeLabel}>
                                        <TooltipValue>{sizeValue}</TooltipValue>
                                    </Tooltip>
                                )}
                            </ValueContainer>
                        </FactsTableRow>
                        <FactsTableRow
                            label={
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                    }}
                                >
                                    <Tooltip title='Teckningskurs'>
                                        <ShowChartIcon fontSize='small' />
                                    </Tooltip>
                                </Box>
                            }
                            padding='2px 0px'
                            leftColumnWidth={null}
                            rightColumnWidth={null}
                        >
                            <ValueContainer>
                                {isLoading && <FadedSkeleton variant='rectangular' width={150} />}
                                {!isLoading && (
                                    <Tooltip title='Teckningskurs'>
                                        <TooltipValue>{subPriceValue}</TooltipValue>
                                    </Tooltip>
                                )}
                            </ValueContainer>
                        </FactsTableRow>
                        <FactsTableRow
                            label={
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                    }}
                                >
                                    <Tooltip title={relevantDateLabel}>
                                        <CalendarMonthOutlinedIcon fontSize='small' />
                                    </Tooltip>
                                </Box>
                            }
                            padding='2px 0px'
                            leftColumnWidth={null}
                            rightColumnWidth={null}
                        >
                            <ValueContainer>
                                {isLoading && <FadedSkeleton variant='rectangular' width={200} />}
                                {!isLoading && (
                                    <Tooltip title={relevantDateLabel}>
                                        <TooltipValue>{relevantDateValue}</TooltipValue>
                                    </Tooltip>
                                )}
                            </ValueContainer>
                        </FactsTableRow>
                        {commitValue ? (
                            <>
                                <FactsTableRow
                                    label={
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {t('components.issueWarrantSummary.secured')}
                                        </Typography>
                                    }
                                    padding='15px 0px 2px 0px'
                                    leftColumnWidth={null}
                                    rightColumnWidth={null}
                                >
                                    <ValueContainer>
                                        {isLoading && <FadedSkeleton variant='rectangular' width={180} />}
                                        {!isLoading && commitValue}
                                    </ValueContainer>
                                </FactsTableRow>
                            </>
                        ) : null}
                        {outcomeValue && (
                            <FactsTableRow
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('components.issueWarrantSummary.outcome')}
                                    </Typography>
                                }
                                padding='0px 0px 8px 0px'
                                leftColumnWidth={null}
                                rightColumnWidth={null}
                            >
                                <ValueContainer>
                                    {isLoading && <FadedSkeleton variant='rectangular' width={180} />}
                                    {!isLoading && outcomeValue}
                                </ValueContainer>
                            </FactsTableRow>
                        )}
                        {noncashConsiderationValue ? (
                            <FactsTableRow
                                label={
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                        }}
                                    >
                                        <Tooltip title='Apportegendom'>
                                            <Target size={18} />
                                        </Tooltip>
                                    </Box>
                                }
                                padding='2px 0px'
                                leftColumnWidth={null}
                                rightColumnWidth={null}
                            >
                                <ValueContainer>
                                    <Tooltip title='Apportegendom'>
                                        <TooltipValue>{noncashConsiderationValue}</TooltipValue>
                                    </Tooltip>
                                </ValueContainer>
                            </FactsTableRow>
                        ) : null}
                    </FactsTable>
                </FactsTableRow>
                {Object.keys(additionalRows).map((label) => (
                    <FactsTableRow key={label} label={label} rightColumnWidth={isSlim ? null : undefined}>
                        {additionalRows[label]}
                    </FactsTableRow>
                ))}
            </FactsTable>
            {hasLink && (
                <Button
                    endIcon={<OpenInNewIcon />}
                    onClick={() => window.open(detailLink, '_blank')}
                    variant='outlined'
                >
                    Öppna {typeLabel.toLocaleLowerCase()}
                </Button>
            )}
        </InfoBlock>
    )
}
